import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material/material.module';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { SolutionsAppService } from 'src/app/services/solutions-app.service';
import { ShowDialogsComponent } from '../show-dialogs/show-dialogs.component';

interface ReadableCheck {
  pre: string;
  columns: string[];
  post: string;
  failMsg?: string;
}

interface ReadableBlock {
  icon: string;
  scope: string;
  scopeCode: string;
  checks: ReadableCheck[];
}

@Component({
  selector: 'app-json-input',
  standalone: true,
  imports: [CommonModule, MaterialModule, FormsModule],
  template: `
    <div class="jv-head">
      <div class="jv-t">Data validation rules</div>
      <div class="jv-s">
        Uploads to <span class="jv-chip">{{ data?.data_name }}</span> are
        checked against these rules before the pipeline runs.
      </div>
    </div>

    <div class="jv-body">
      <!-- JSON editor -->
      <div class="jv-editor" [class.err]="jsonData && !isValid">
        <textarea
          id="jvArea"
          [(ngModel)]="jsonData"
          (ngModelChange)="onJsonChange()"
          rows="9"
          spellcheck="false"
          placeholder='Paste the validation rules JSON — e.g. {"required_columns": ["part_no"], "constraints": {"price": {"min": 0}}}'
          class="jv-area"
        ></textarea>
        <div class="jv-status">
          <span class="jv-ok" *ngIf="isValid">✓ Valid JSON</span>
          <span class="jv-bad" *ngIf="jsonData && !isValid">{{ errors }}</span>
          <span class="jv-hint" *ngIf="!jsonData"
            >required_columns · data_types · constraints · custom_validations</span
          >
          <span class="jv-spacer"></span>
          <button
            type="button"
            class="jv-fmt"
            [disabled]="!isValid"
            (click)="formatJson()"
          >
            Format
          </button>
        </div>
      </div>

      <!-- Plain-English rendering -->
      <div class="jv-read" *ngIf="isValid && hasRules">
        <div class="jv-read-h">What these rules mean</div>

        <div class="rr" *ngFor="let block of readableBlocks">
          <div class="rr-t">
            <span class="rr-ic">{{ block.icon }}</span>{{ block.scope }}
            <span class="rr-col" *ngIf="block.scopeCode">{{
              block.scopeCode
            }}</span>
          </div>
          <div class="rr-check" *ngFor="let c of block.checks">
            <div class="rr-line">
              <ng-container *ngIf="c.pre">{{ c.pre }} </ng-container>
              <span class="rr-col" *ngFor="let col of c.columns">{{
                col
              }}</span>
              <ng-container *ngIf="c.post"> {{ c.post }}</ng-container>
            </div>
            <div class="rr-fail" *ngIf="c.failMsg">
              On failure the customer sees: <em>“{{ c.failMsg }}”</em>
            </div>
          </div>
        </div>
      </div>

      <div class="jv-read empty" *ngIf="!hasRules">
        <div class="jv-read-h">What these rules mean</div>
        <div class="rr-dim">
          Paste valid JSON above and a plain-English summary appears here.
        </div>
      </div>
    </div>

    <div class="jv-foot">
      <span class="jv-note">Validation errors are shown to the customer at upload time.</span>
      <span class="jv-spacer"></span>
      <button type="button" class="btn-ghost" (click)="closeJsonInput()">
        Close
      </button>
      <button
        type="button"
        class="btn-primary"
        cdkFocusInitial
        [disabled]="!isValid"
        (click)="saveDataValidationConfiguration()"
      >
        {{ alreadyExist ? 'Update rules' : 'Save rules' }}
      </button>
    </div>
  `,
  styleUrls: ['./json-input.component.scss'],
})
export class JsonInputComponent implements OnInit {
  jsonData: string = '';
  parsedJson: any;
  errors: any = '';
  alreadyExist = false;
  isValid = false;
  private originalJson = '';

  private typeLabels: { [key: string]: string } = {
    str: 'text',
    string: 'text',
    int: 'whole numbers',
    integer: 'whole numbers',
    float: 'decimal numbers',
    number: 'numbers',
    bool: 'true / false values',
    boolean: 'true / false values',
    date: 'dates',
    datetime: 'dates & times',
  };

  private knownKeys = [
    'required_columns',
    'data_types',
    'constraints',
    'custom_validations',
  ];

  constructor(
    private dialogRef: MatDialogRef<JsonInputComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private snackBar: SharedFeaturesService,
    private solutionServices: SolutionsAppService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.solutionServices
      .getDataValidationConfig(
        this.data?.solution_id,
        this.data?.version_id,
        this.data?.data_name
      )
      .subscribe({
        next: (response: any) => {
          const config = response?.msg?.[0]?.config;
          if (config) {
            this.jsonData = JSON.stringify(config, null, 2);
            this.alreadyExist = true;
          }
          this.originalJson = this.jsonData || '';
          this.onJsonChange();
        },
        error: (error: any) => {
          console.error('an error occurred', error);
        },
      });
  }

  get hasRules(): boolean {
    return (
      this.isValid &&
      this.parsedJson &&
      typeof this.parsedJson === 'object' &&
      Object.keys(this.parsedJson).length > 0
    );
  }

  /**
   * Unified plain-English view model. Understands both config schemas:
   * 1. Sheet-block array: [{sheet_identifier?, validation: [{validation_name, params}]}]
   * 2. Flat object: {required_columns, data_types, constraints, custom_validations}
   */
  get readableBlocks(): ReadableBlock[] {
    const cfg = this.parsedJson;
    if (!cfg) return [];
    if (Array.isArray(cfg)) {
      return cfg.map((block: any, i: number) => this.sheetBlock(block, i));
    }
    if (typeof cfg === 'object') {
      return this.flatObjectBlocks(cfg);
    }
    return [
      {
        icon: '⚙',
        scope: 'Configuration',
        scopeCode: '',
        checks: [{ pre: 'Kept as written (not a rules object)', columns: [], post: '' }],
      },
    ];
  }

  /* ---- schema 1: sheet-block array ---- */

  private sheetBlock(block: any, index: number): ReadableBlock {
    const validators = Array.isArray(block?.validation) ? block.validation : [];
    const id = block?.sheet_identifier;
    const fileLevel =
      !id &&
      validators.every(
        (v: any) => v?.validation_name === 'validate_sheets_of_file'
      );
    return {
      icon: fileLevel ? '▤' : '▦',
      scope: fileLevel
        ? 'Whole workbook'
        : id
        ? 'Sheets matching'
        : `Sheet group ${index + 1}`,
      scopeCode: id || '',
      checks: validators.length
        ? validators.map((v: any) => this.validatorCheck(v))
        : [{ pre: 'No checks defined for this group', columns: [], post: '' }],
    };
  }

  private validatorCheck(v: any): ReadableCheck {
    const p = v?.params || {};
    const name = v?.validation_name || '';
    const cols: string[] = Array.isArray(p.column_names)
      ? p.column_names
      : p.column_names
      ? [p.column_names]
      : [];
    const failMsg = p.error_message || '';

    switch (name) {
      case 'validate_sheets_of_file':
        return {
          pre: 'The file must contain the expected sheets',
          columns: [],
          post: '',
          failMsg,
        };
      case 'validate_mandatory_columns':
        return {
          pre: 'Must include columns',
          columns: Array.isArray(p.mandatory_columns)
            ? p.mandatory_columns
            : [],
          post: '',
          failMsg,
        };
      case 'validate_not_empty':
        return { pre: '', columns: cols, post: 'must not have empty cells', failMsg };
      case 'validate_unique':
        return {
          pre: '',
          columns: cols,
          post: 'must not contain duplicate values',
          failMsg,
        };
      case 'validate_single_unique_value':
        return {
          pre: '',
          columns: cols,
          post: 'must contain one single value throughout',
          failMsg,
        };
      case 'validate_type': {
        const label =
          this.typeLabels[String(p.expected_type).toLowerCase()] ||
          `values of type "${p.expected_type}"`;
        return { pre: '', columns: cols, post: `must contain ${label}`, failMsg };
      }
      case 'validate_values_in_columns':
        return {
          pre: '',
          columns: cols,
          post: `may only contain: ${(p.allowed_values || []).join(', ')}`,
          failMsg,
        };
      case 'validate_string_or_empty':
        return { pre: '', columns: cols, post: 'must be text or left empty', failMsg };
      case 'validate_sub_assembly_match':
        return {
          pre: '',
          columns: cols,
          post: "must match the sheet's name",
          failMsg,
        };
      case 'validation_percent_column':
        return {
          pre: 'When',
          columns: [p.columns_to_check].filter(Boolean),
          post: `is ${(p.column_values || []).join(' or ')}, “${
            p.value_column_to_check || 'the value'
          }” must not exceed 100%`,
          failMsg,
        };
      default:
        return {
          pre: this.prettifyName(name),
          columns: cols,
          post: '',
          failMsg,
        };
    }
  }

  private prettifyName(name: string): string {
    if (!name) return 'Unnamed check';
    const words = name.replace(/^valid(ation|ate)_/, '').replace(/_/g, ' ');
    return `Check: ${words}`;
  }

  /* ---- schema 2: flat object ---- */

  private flatObjectBlocks(cfg: any): ReadableBlock[] {
    const checks: ReadableCheck[] = [];
    if (Array.isArray(cfg.required_columns) && cfg.required_columns.length) {
      checks.push({
        pre: 'Must include columns',
        columns: cfg.required_columns.map((c: any) => String(c)),
        post: '',
      });
    }
    if (cfg.data_types && typeof cfg.data_types === 'object') {
      Object.entries(cfg.data_types).forEach(([column, t]) => {
        const label =
          this.typeLabels[String(t).toLowerCase()] || `values of type "${t}"`;
        checks.push({ pre: '', columns: [column], post: `must contain ${label}` });
      });
    }
    if (cfg.constraints && typeof cfg.constraints === 'object') {
      Object.entries(cfg.constraints).forEach(([column, c]: [string, any]) => {
        const hasMin = c?.min !== undefined && c?.min !== null;
        const hasMax = c?.max !== undefined && c?.max !== null;
        let text = 'unrestricted';
        if (hasMin && hasMax) text = `between ${c.min} and ${c.max}`;
        else if (hasMin) text = `at least ${c.min}`;
        else if (hasMax) text = `at most ${c.max}`;
        checks.push({ pre: '', columns: [column], post: `must be ${text}` });
      });
    }
    if (Array.isArray(cfg.custom_validations)) {
      cfg.custom_validations.forEach((c: any) => {
        checks.push({
          pre: 'Custom check:',
          columns: [c?.rule || '(no rule)'],
          post: '',
          failMsg: c?.error_message || '',
        });
      });
    }
    const blocks: ReadableBlock[] = [];
    if (checks.length) {
      blocks.push({ icon: '✓', scope: 'All uploads', scopeCode: '', checks });
    }
    const extras = Object.keys(cfg).filter((k) => !this.knownKeys.includes(k));
    if (extras.length) {
      blocks.push({
        icon: '⚙',
        scope: 'Advanced settings',
        scopeCode: '',
        checks: [
          {
            pre: '',
            columns: extras,
            post: '— not summarised, kept as written',
          },
        ],
      });
    }
    return blocks;
  }

  onJsonChange() {
    if (!this.jsonData?.trim()) {
      this.parsedJson = null;
      this.errors = '';
      this.isValid = false;
      return;
    }
    try {
      this.parsedJson = JSON.parse(this.jsonData);
      this.errors = '';
      this.isValid = true;
    } catch (error: any) {
      this.parsedJson = null;
      this.errors = error?.message || 'Invalid JSON';
      this.isValid = false;
    }
  }

  formatJson() {
    if (!this.isValid) return;
    this.jsonData = JSON.stringify(this.parsedJson, null, 2);
  }

  saveDataValidationConfiguration() {
    try {
      this.parsedJson = JSON.parse(this.jsonData);
      this.errors = '';
      const payload = {
        dataset_name: this.data?.data_name,
        solution_id: this.data?.solution_id,
        version_id: this.data?.version_id,
        config: this.parsedJson,
      };

      if (this.alreadyExist) {
        this.solutionServices
          .putDataValidationConfig(
            this.data?.solution_id,
            this.data?.version_id,
            this.data?.data_name,
            payload
          )
          .subscribe({
            next: (response: any) => {
              this.snackBar.openSnackBar(
                response?.msg,
                'Close',
                'success-snackbar'
              );
              this.dialogRef.close();
            },
            error: (error: any) => {
              console.error('An error occurred', error);
              this.snackBar.openSnackBar(
                JSON.stringify(error),
                'Close',
                'error-snackbar'
              );
            },
          });
      } else {
        this.solutionServices.saveDataValidationConfig(payload).subscribe({
          next: (response: any) => {
            this.snackBar.openSnackBar(
              response?.msg,
              'Close',
              'success-snackbar'
            );
            this.dialogRef.close();
          },
          error: (error: any) => {
            console.error('an error occurred', error);
            this.snackBar.openSnackBar(
              error?.error?.msg ||
                error?.message ||
                'Could not save the validation rules. Please try again.',
              'Close',
              'error-snackbar'
            );
          },
        });
      }
    } catch (error) {
      this.errors = error;
      this.snackBar.openSnackBar('Invalid JSON', 'Close', 'error-snackbar');
    }
  }

  closeJsonInput() {
    if ((this.jsonData || '') === this.originalJson) {
      this.dialogRef.close();
      return;
    }
    const dialogRef = this.dialog.open(ShowDialogsComponent, {
      data: {
        title: 'Discard changes?',
        message: 'Your changes to the validation rules will be lost.',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
        danger: true,
      },
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogRef.close();
      }
    });
  }
}
